import { useContext } from 'react';
import { format } from 'date-fns';
import useAppUser from 'hooks/useAppUser';
import { getUnitType, isLeadAMainBooking } from 'utils/lead/leadUtils';
import { convertQuoteOverrideToLeadQuoteOverride } from 'utils/quote/quoteUtils';
import {
  Lead,
  LeadGuestInformation,
  LeadSavingPayload,
  LeadStatus,
  LeadType,
  LeadUpdateInternalPayload,
} from '../../../../models/Leads';
import LeadModalContext from './LeadModalContext';

function getLeadType(status: LeadStatus) {
  if (status === LeadStatus.BLOCKED) {
    return LeadType.BLOCK;
  }

  if (status === LeadStatus.NEW || status === LeadStatus.ON_HOLD) {
    return LeadType.INQUIRY;
  }

  return LeadType.BOOKING;
}

const createGuestInformation = (leadSavingPayload: LeadSavingPayload) => {
  const keys = [
    'firstName',
    'lastName',
    'address1',
    'address2',
    'zipCode',
    'city',
    'state',
    'countryCode',
    'passportId',
    'passportCountryCode',
    'adultCount',
    'childrenCount',
    'petCount',
    'infantCount',
    'riskScore',
    'email',
    'secondaryEmail',
    'phoneNumber',
    'cellPhoneNumber',
    'flightNumber',
    'reasonForTrip',
    'willHaveParty',
    'willHaveVisit',
    'preferredCurrency',
    'preferredLocale',
  ];

  const guestInformation = {} as LeadGuestInformation;

  keys.forEach((key) => {
    if (key === 'address1') {
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      guestInformation.address1 = leadSavingPayload.address;
      delete leadSavingPayload.address;
    }

    // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    if (leadSavingPayload[key] !== undefined) {
      // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      guestInformation[key] = leadSavingPayload[key];
      // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      delete leadSavingPayload[key];
    }
  });

  return guestInformation;
};

const useLeadSavingPayload = (lead: Lead) => {
  const { agencyUid } = useAppUser();
  const { availableProperties, overrides, setOverrides } =
    useContext(LeadModalContext);

  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const getLeadCreateSavePayload = (leadFormValues): LeadSavingPayload => {
    const leadSavingPayload = { ...leadFormValues };
    delete leadSavingPayload.cellPhoneNumber_areaCode;
    delete leadSavingPayload.cellPhoneNumber_base;
    delete leadSavingPayload.phoneNumber_areaCode;
    delete leadSavingPayload.phoneNumber_base;
    delete leadSavingPayload.pinCode;
    delete leadSavingPayload.reviewByHost;
    delete leadSavingPayload.tags;
    delete leadSavingPayload.uid;
    delete leadSavingPayload.guest;
    delete leadSavingPayload.creationMetadata;
    delete leadSavingPayload.fees;

    const unitType = getUnitType(
      availableProperties,
      leadSavingPayload.propertyUid,
    );

    if (overrides) {
      leadSavingPayload.quoteOverrides = { ...overrides };
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      setOverrides(null);
    }

    delete leadSavingPayload.overrides;
    delete leadSavingPayload.force100PercentPaymentAtReservation;

    if (!leadSavingPayload.leadType) {
      leadSavingPayload.leadType = getLeadType(leadSavingPayload.status);
    }

    // legacy logic for handling deprecated status
    if (leadSavingPayload.status === LeadStatus.PAID_IN_FULL) {
      delete leadSavingPayload.status;
    }

    if (
      Object.keys(leadSavingPayload).includes('assigneeUid') &&
      !leadSavingPayload.assigneeUid
    ) {
      leadSavingPayload.assigneeUid = null; // do not send over an empty string
      leadSavingPayload.fieldsToClear = ['assigneeUid'];
    }

    leadSavingPayload.guestInformation =
      createGuestInformation(leadSavingPayload);

    if (leadSavingPayload.checkInDate) {
      leadSavingPayload.checkInLocalDate = format(
        leadSavingPayload.checkInDate,
        'yyyy-MM-dd',
      );

      delete leadSavingPayload.checkInDate;
    }

    if (leadSavingPayload.checkOutDate) {
      leadSavingPayload.checkOutLocalDate = format(
        leadSavingPayload.checkOutDate,
        'yyyy-MM-dd',
      );
      delete leadSavingPayload.checkOutDate;
    }

    if (leadSavingPayload.arrivalDate) {
      leadSavingPayload.guestInformation.arrivalLocalDateTime = format(
        leadSavingPayload.arrivalDate,
        "yyyy-MM-dd'T'HH:mm:ss",
      );
      delete leadSavingPayload.arrivalDate;
    }

    if (leadSavingPayload.departureDate) {
      leadSavingPayload.guestInformation.departureLocalDateTime = format(
        leadSavingPayload.departureDate,
        "yyyy-MM-dd'T'HH:mm:ss",
      );
      delete leadSavingPayload.departureDate;
    }

    leadSavingPayload.agencyUid = agencyUid;

    if (unitType) {
      leadSavingPayload.unitUid = leadSavingPayload.propertyUid;
      leadSavingPayload.propertyUid = unitType.uid;
    }

    return leadSavingPayload;
  };

  const getLeadUpdateSavePayload = (
    // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    leadFormValues,
  ): LeadUpdateInternalPayload => {
    const leadSavingPayload = { ...leadFormValues };

    const unitType = getUnitType(
      availableProperties,
      leadSavingPayload.propertyUid,
    );

    const payload = {} as LeadUpdateInternalPayload;

    if (unitType) {
      payload.unitUid = leadSavingPayload.propertyUid;
      payload.propertyUid = unitType.uid;
    } else {
      payload.propertyUid = leadSavingPayload.propertyUid;
    }

    payload.guestInformation = createGuestInformation(leadSavingPayload);

    if (leadSavingPayload.checkInDate) {
      payload.checkInLocalDate = format(
        leadSavingPayload.checkInDate,
        'yyyy-MM-dd',
      );
    }

    if (leadSavingPayload.checkOutDate) {
      payload.checkOutLocalDate = format(
        leadSavingPayload.checkOutDate,
        'yyyy-MM-dd',
      );
    }

    if (leadSavingPayload.arrivalDate && !isLeadAMainBooking(lead)) {
      payload.guestInformation.arrivalLocalDateTime = format(
        leadSavingPayload.arrivalDate,
        "yyyy-MM-dd'T'HH:mm:ss",
      );
    }

    if (leadSavingPayload.departureDate && !isLeadAMainBooking(lead)) {
      payload.guestInformation.departureLocalDateTime = format(
        leadSavingPayload.departureDate,
        "yyyy-MM-dd'T'HH:mm:ss",
      );
    }

    if (
      Object.keys(leadSavingPayload).includes('assigneeUid') &&
      !leadSavingPayload.assigneeUid
    ) {
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      payload.assigneeUid = null; // do not send over an empty string
    }

    payload.status = leadSavingPayload.status;
    payload.assigneeUid = leadSavingPayload.assigneeUid || null;
    payload.notes = leadSavingPayload.notes;
    payload.extraNotes = leadSavingPayload.extraNotes;
    payload.attributeToVrbo = leadSavingPayload.attributeToVrbo;

    if (overrides) {
      payload.quoteOverrides = convertQuoteOverrideToLeadQuoteOverride({
        ...overrides,
      });
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      setOverrides(null);
    }

    return payload;
  };

  // The API now expect different payloads for creating and updating leads.
  return {
    getLeadCreateSavePayload,
    getLeadUpdateSavePayload,
  };
};

export default useLeadSavingPayload;
